import React, { useMemo, useRef } from 'react';
import ReactDOM from "react-dom";
import {get, isEmpty} from "lodash";
import { useStoreState } from "easy-peasy";
import {useTranslation} from "react-i18next";
import {showOrHide} from "../lib/utils/helpers";

function GeneralMotorData() {
  const { t } = useTranslation();

  const fan = useStoreState(state => state.selection.fan);
  const { metadata } = useStoreState(state => state.selection.servicePoint);
  const showAlternativePoint = useStoreState(state => state.selection.showAlternativePoint);

  const motor = useMemo(() => {
    const { motor } = showAlternativePoint ? get(metadata, "alternative_service_points", {}) : get(metadata, "service_points", {});

    if (!isEmpty(motor)) {
      return motor;
    }

    return fan.hasMotor ? fan.motor : {};
  }, [fan, metadata, showAlternativePoint]);

  const capacitor = useMemo(() => {
    return motor ? motor.capacitor : null;
  }, [motor]);

  /* Reuse same ref on every render
   * https://github.com/facebook/react/issues/15110
   */
  const menuRef = useRef(null);

  if (!menuRef.current) {
    // Create once
    menuRef.current = document.getElementById("general-motor-data");
    menuRef.current.innerHTML = "";
  }

  const domElement = menuRef.current;

  //the fan.power and an.powerString value below is from fan motor
  return ReactDOM.createPortal(
    <>
      { (!isEmpty(motor) || capacitor || showOrHide(fan.power)) &&
        <>
          <div className="col-xs-12"><h2>{ t('frontend:fan.motor') }</h2></div>
          {
            showOrHide(fan.power) &&
            <div className="col-xs-6 col-md-3 col-lg-2">
              <div className="panel panel-info">
                <div className="panel-heading">
                  { fan.type === 4 || fan.type === 6 ? t("frontend:fan.max_power") : t("frontend:fan.power") }
                </div>
                <div className="panel-body">
                  { fan.powerString } kW
                </div>
              </div>
            </div>
          }
          {
            (showOrHide(motor.power) && !fan.hasMotor) &&
            <div className="col-xs-6 col-md-3 col-lg-2">
              <div className="panel panel-info">
                <div className="panel-heading">
                  { t('frontend:fan.power') }
                </div>
                <div className="panel-body">
                  { motor.power_string } kW
                </div>
              </div>
            </div>
          }
          {
            showOrHide(motor.rpm) &&
            <div className="col-xs-6 col-md-3 col-lg-2">
              <div className="panel panel-info">
                <div className="panel-heading">
                  { t('frontend:fan.rpm_motor') }
                </div>
                <div className="panel-body">
                  { motor.rpm_string }
                </div>
              </div>
            </div>
          }
          {
            showOrHide(motor.imax_110) &&
            <div className="col-xs-6 col-md-3 col-lg-2">
              <div className="panel panel-info">
                <div className="panel-heading">
                  I max. 110 V
                </div>
                <div className="panel-body">{ motor.imax_110 } A</div>
              </div>
            </div>
          }
          {
            showOrHide(motor.imax_220) &&
            <div className="col-xs-6 col-md-3 col-lg-2">
              <div className="panel panel-info">
                <div className="panel-heading">
                  I max. 220 V
                </div>
                <div className="panel-body">{ motor.imax_220 } A</div>
              </div>
            </div>
          }
          {
            showOrHide(motor.imax_230) &&
            <div className="col-xs-6 col-md-3 col-lg-2">
              <div className="panel panel-info">
                <div className="panel-heading">
                  I max. 230 V
                </div>
                <div className="panel-body">{ motor.imax_230 } A</div>
              </div>
            </div>
          }
          {
            showOrHide(motor.imax_400) &&
            <div className="col-xs-6 col-md-3 col-lg-2">
              <div className="panel panel-info">
                <div className="panel-heading">
                  I max. 400 V
                </div>
                <div className="panel-body">{ motor.imax_400 } A</div>
              </div>
            </div>
          }
          {
            showOrHide(motor.imax_460) &&
            <div className="col-xs-6 col-md-3 col-lg-2">
              <div className="panel panel-info">
                <div className="panel-heading">
                  I max. 400-460 V
                </div>
                <div className="panel-body">{ motor.imax_460 } A</div>
              </div>
            </div>
          }
          {
            showOrHide(motor.imax_690) &&
            <div className="col-xs-6 col-md-3 col-lg-2">
              <div className="panel panel-info">
                <div className="panel-heading">
                  I max. 690 V
                </div>
                <div className="panel-body">{ motor.imax_690 } A</div>
              </div>
            </div>
          }
          {
            showOrHide(motor.size) &&
            <div className="col-xs-6 col-md-3 col-lg-2">
              <div className="panel panel-info">
                <div className="panel-heading">
                  { t("fanware:fans.tabs.general_data.size")}
                </div>
                <div className="panel-body">{ motor.size }</div>
              </div>
            </div>
          }
          {
            showOrHide(motor.weight) &&
            <div className="col-xs-6 col-md-3 col-lg-2">
              <div className="panel panel-info">
                <div className="panel-heading">
                  { t("fanware:fans.tabs.general_data.weight")}
                </div>
                <div className="panel-body">{ motor.weight } Kg</div>
              </div>
            </div>
          }
          {
            showOrHide(motor.efficiency) &&
            <div className="col-xs-6 col-md-3 col-lg-2">
              <div className="panel panel-info">
                <div className="panel-heading">
                  { t("fanware:fans.tabs.general_data.efficiency")}
                </div>
                <div className="panel-body">{ motor.efficiency } %</div>
              </div>
            </div>
          }
          {
            showOrHide(motor.fp) &&
            <div className="col-xs-6 col-md-3 col-lg-2">
              <div className="panel panel-info">
                <div className="panel-heading">
                  { t("fanware:fans.tabs.general_data.fp")}
                </div>
                <div className="panel-body">{ motor.fp }</div>
              </div>
            </div>
          }
          {
            showOrHide(capacitor) &&
            <div className="col-xs-6 col-md-3 col-lg-2">
              <div className="panel panel-info">
                <div className="panel-heading">
                  { t("fanware:fans.capacitor")}
                </div>
                <div className="panel-body">{ capacitor }</div>
              </div>
            </div>
          }
        </>
      }
    </>
    , domElement
  );
}

export default GeneralMotorData;
